// Libredesk 客服挂件。
//
// 模型：挂件在 App 启动时就以**游客身份**挂上（登录页也有），拿到
// /auth/me 的 libredeskToken 后调 identifyLibredesk() 升级为登录用户，
// 登出时调 signOutLibredesk() 退回游客态。全程只有一个挂件实例，不重建。
//
// 以下几点是读 widget.js 源码确认的：
//
// 1) 脚本开头 `if (window.__libredeskWidgetLoaded) return;` —— 每页只执行一次。
//    "删掉 script 标签再加回来"不会重新初始化，所以身份变化一律走它的 API。
// 2) 脚本末尾的 autoInit 会读 window.LibredeskSettings 自动初始化一次。这里
//    **不设**这个全局量，改为加载完自己调 window.initLibredesk(config)，
//    时机可控，也不会和 autoInit 撞成 "already initialized"。
// 3) 不传 userJWT 时挂件会回落到 visitor/session cookie 建匿名会话 —— 游客模式
//    是它原生支持的路径，不是绕过去的。
//
// 挂件暴露的 API：window.initLibredesk(config)、以及实例上的
// setUser(jwt) / logout() / show() / hide() / destroy()、onShow / onHide / onUnreadCountChange。
// ⚠️ show() / hide() 是 showChat() / hideChat() 的别名，只开关聊天窗口；启动按钮本身的显隐
// 由实例的 hideLauncher 标志 + updateLauncherVisibility() 决定（见 hideLibredeskLauncher）。
//
// 语言：config.language 定挂件的初始语言，实例上的 setLanguage(code) 运行时切换（不重建挂件，
// 聊天窗口里的会话不断）。这两个是上游 abhinavxd/libredesk#668 加的，v2.8.0 / v2.9.0-rc.8 都没有：
// 旧挂件会忽略不认识的 language，实例上也没有 setLanguage，所以这里传了也没有副作用，
// 等 Libredesk 升到带它的版本就自动生效。
// ⚠️ 只有 inbox 的语言设成 auto 时宿主页传的语言才生效；inbox 固定了语言就一律用固定的。

import { libredeskBaseUrl, libredeskInboxId } from "@/utils/env.js";
import { onOverlayChange } from "@/utils/overlayWatcher.js";

const SCRIPT_ID = "libredesk-widget-script";

// widget.js 只需加载一次，用 Promise 去重并发调用。
let scriptPromise = null;
let mounted = false;

// iframe 里的 Vue 应用是否已就绪。
//
// 为什么必须管这个：挂件的 postToIframe 只检查 iframe 存在，不检查里面的应用
// 是否 ready（见 widget.js 的 postToIframe），而它自己发 SET_JWT_TOKEN 是等
// iframe 回报 VUE_APP_READY 之后才做的。我们在外部调 setUser 没有这层保护，
// 刷新页面后 Layout created 几乎立刻就会拿到 token，这时 iframe 里的应用大概率
// 还没起来，消息会被丢掉。所以这里自己听同一个信号，没就绪就先排队。
let widgetReady = false;
let pendingJwt = null;
let messageListenerBound = false;

// dashboard 的语言（localStorage.language，见 src/lang/index.js）→ 挂件语言包的代码。
// 不认识的值回落英文，和 dashboard 自己没选过语言时默认 en 一致。
const LIBREDESK_LANGUAGES = { en: "en-US", zh: "zh-CN", de: "de-DE", it: "it-IT" };
function libredeskLanguage(lang) {
  return LIBREDESK_LANGUAGES[lang] || LIBREDESK_LANGUAGES.en;
}

export function isLibredeskConfigured() {
  return !!(libredeskBaseUrl() && libredeskInboxId());
}

function onWidgetMessage(event) {
  // iframe 发给 parent 的 postMessage 任何人都收得到，按 origin 过滤即可。
  if (event.origin !== libredeskBaseUrl()) return;
  const type = event.data && event.data.type;
  if (type !== "VUE_APP_READY" && type !== "WIDGET_LOADED") return;
  widgetReady = true;
  if (pendingJwt) {
    const jwt = pendingJwt;
    pendingJwt = null;
    sendUser(jwt);
  }
}

function sendUser(jwt) {
  const instance = window.Libredesk;
  if (!instance || typeof instance.setUser !== "function") return;
  try {
    // ⚠️ 已知差异：挂件初始化路径发的是
    //      { type:'SET_JWT_TOKEN', jwt, visitorToken }
    //    而 setUser 只发 { type:'SET_JWT_TOKEN', jwt } —— 少了 visitorToken。
    //    visitorToken 用于把游客期间的匿名会话接到登录用户身上，所以"游客先提问、
    //    再登录"这条路径能不能续上会话，需要联调时实测。
    //    若确认接不上，替换成下面这行即可（用的是实例内部方法，比 setUser 脆）：
    //      instance.postToIframe({ type:'SET_JWT_TOKEN', jwt,
    //        visitorToken: instance.getCookie(instance.getCookieName('visitor')) || '' });
    instance.setUser(jwt);
  } catch (e) {
    console.warn("[libredesk] setUser failed:", e && e.message);
  }
}

function loadWidgetScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (document.getElementById(SCRIPT_ID)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = libredeskBaseUrl() + "/widget.js";
    script.onload = () => resolve();
    script.onerror = () => {
      // 允许后续重试：第三方脚本挂了不该让挂件永久失效。
      scriptPromise = null;
      if (script.parentNode) script.parentNode.removeChild(script);
      reject(new Error("libredesk widget.js failed to load"));
    };
    document.body.appendChild(script);
  });
  return scriptPromise;
}

/**
 * 以游客身份挂载挂件。App 启动时调一次即可（见 App.vue 的 created）。
 * 重复调用无副作用。
 */
export function mountLibredesk() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.resolve();
  }
  if (!isLibredeskConfigured() || mounted) return Promise.resolve();
  mounted = true;

  // 监听必须先于挂件创建绑定，否则 VUE_APP_READY 可能在绑定之前就发出来了。
  if (!messageListenerBound) {
    window.addEventListener("message", onWidgetMessage);
    messageListenerBound = true;
  }

  return loadWidgetScript()
    .then(() => {
      // 不传 userJWT = 游客模式。
      // language 等脚本加载完才读：加载期间用户切过语言的话，用的是切完的那个。
      const instance = window.initLibredesk({
        baseURL: libredeskBaseUrl(),
        inboxID: libredeskInboxId(),
        language: libredeskLanguage(localStorage.getItem("language") || "en"),
      });
      whenLauncherReady(instance, setupLauncherPeek);
      // 全局规则：任何弹窗 / 抽屉 / 黑色遮罩开着时都藏起启动按钮（和各抽屉手动调用共用同一个引用计数）
      whenLauncherReady(instance, () => {
        let hiddenByOverlay = false;
        onOverlayChange((open) => {
          if (open === hiddenByOverlay) return;
          hiddenByOverlay = open;
          if (open) hideLibredeskLauncher();
          else showLibredeskLauncher();
        });
      });
    })
    .catch((e) => {
      mounted = false;
      console.warn("[libredesk] mount failed:", e && e.message);
    });
}

/**
 * 把当前游客会话升级为登录用户。
 * @param {string|null} token /auth/me 的 libredeskToken，为空表示后端没签发 —— 保持游客态。
 */
export function identifyLibredesk(token) {
  if (typeof window === "undefined") return;
  if (!isLibredeskConfigured() || !token) return;
  if (!widgetReady) {
    // iframe 还没就绪，排队等 VUE_APP_READY。
    pendingJwt = token;
    return;
  }
  sendUser(token);
}

/**
 * 登出：退回游客态，而不是销毁挂件（登录页仍然要有客服入口）。
 * 挂件的 logout() 会清掉 session / visitor 两个 cookie 并让 iframe 清会话。
 */
export function signOutLibredesk() {
  if (typeof window === "undefined") return;
  pendingJwt = null;
  const instance = window.Libredesk;
  if (!instance || typeof instance.logout !== "function") return;
  try {
    instance.logout();
  } catch (e) {
    console.warn("[libredesk] logout failed:", e && e.message);
  }
}

/**
 * dashboard 切语言时让挂件跟着切。凡是改 localStorage.language 的地方都要调它。
 * 挂件还没初始化时什么都不用做：mountLibredesk 初始化时读的就是当时的 localStorage.language。
 * setLanguage 在 iframe 还没加载完时会自己记下、加载完再发，所以这里不用像 identifyLibredesk 那样排队。
 * @param {string} lang dashboard 的语言代码（en / zh / de / it）
 */
export function setLibredeskLanguage(lang) {
  if (typeof window === "undefined") return;
  const instance = window.Libredesk;
  // 脚本刚加载时 window.Libredesk 是类本身（setLanguage 在原型上，typeof 拿不到）；
  // 旧版挂件（见文件头）的实例上也没有 setLanguage。两种情况都直接跳过。
  if (!instance || typeof instance.setLanguage !== "function") return;
  try {
    instance.setLanguage(libredeskLanguage(lang));
  } catch (e) {
    console.warn("[libredesk] setLanguage failed:", e && e.message);
  }
}

/**
 * 打开聊天窗口（登录页 / 选酒店页的「Contact support」链接用）。
 * @returns {boolean} 打开了返回 true；挂件还不能用（未配置、脚本加载失败、还在加载）返回 false，
 *   调用方据此回落到原来的 mailto。
 */
export function openLibredeskChat() {
  if (typeof window === "undefined" || !isLibredeskConfigured()) return false;
  const instance = window.Libredesk;
  // 脚本刚加载时 window.Libredesk 是类本身（show 在原型上，typeof 拿不到）。
  // widgetLoaded 要等 iframe 里的应用报 WIDGET_LOADED 才置 true，右下角按钮也是这时才出现；
  // 在那之前 show() 只会弹出一个空白窗口（staging 经 NetBird 要 8~10 秒）。
  // 所以和按钮同一个口径：按钮出现了才打开聊天，否则照旧发邮件。
  if (!instance || typeof instance.show !== "function" || !instance.widgetLoaded) return false;
  try {
    instance.show();
    return true;
  } catch (e) {
    console.warn("[libredesk] open chat failed:", e && e.message);
    return false;
  }
}

/**
 * 彻底卸载挂件。目前没有调用方 —— 登出走 signOutLibredesk 即可。
 * 保留它是因为 destroy() 是挂件唯一能清干净的途径：它建的 DOM 没有 id 也没有
 * class，靠选择器删不掉。destroy() 不会清 window.Libredesk 这个实例引用，而
 * window.initLibredesk 判断"是否已初始化"正是看它，所以必须一并删掉。
 */
export function unmountLibredesk() {
  if (typeof window === "undefined") return;
  const instance = window.Libredesk;
  // 脚本刚加载时 window.Libredesk 是类本身，destroy 在原型上，
  // 这个 typeof 检查刚好能区分"类"和"实例"。
  if (instance && typeof instance.destroy === "function") {
    try {
      instance.destroy();
    } catch (e) {
      console.warn("[libredesk] destroy failed:", e && e.message);
    }
    delete window.Libredesk;
  }
  if (messageListenerBound) {
    window.removeEventListener("message", onWidgetMessage);
    messageListenerBound = false;
  }
  mounted = false;
  widgetReady = false;
  pendingJwt = null;
}

// 抽屉打开时临时藏起右下角的启动按钮：它是 position:fixed + z-index 9999，
// 会盖在抽屉内容上。按引用计数，两个抽屉各自开关时不会提前放出来。
//
// 挂件没有公开的"隐藏按钮"接口（见文件头），这里借用实例上的 hideLauncher 标志 +
// updateLauncherVisibility()（读 widget.js 源码确认的；handleWidgetLoaded 也会读它，
// 所以挂件还没加载完就调用也生效）。聊天窗口开着的话一并关掉，关抽屉时不自动重开。
let launcherHideCount = 0;
let launcherHiddenBefore = false;
function widgetInstance() {
  const instance = typeof window !== "undefined" && window.Libredesk;
  // 脚本刚加载时 window.Libredesk 是类本身，方法都在原型上，这个检查能区分类和实例
  return instance && typeof instance.updateLauncherVisibility === "function" ? instance : null;
}
export function hideLibredeskLauncher() {
  launcherHideCount += 1;
  if (launcherHideCount !== 1) return;
  const w = widgetInstance();
  if (!w) return;
  try {
    launcherHiddenBefore = !!w.hideLauncher;
    w.hideLauncher = true;
    if (w.isChatVisible && typeof w.hideChat === "function") w.hideChat();
    w.updateLauncherVisibility();
  } catch (e) {
    console.warn("[libredesk] hide launcher failed:", e && e.message);
  }
}
export function showLibredeskLauncher() {
  if (launcherHideCount === 0) return;
  launcherHideCount -= 1;
  if (launcherHideCount !== 0) return;
  const w = widgetInstance();
  if (!w) return;
  try {
    w.hideLauncher = launcherHiddenBefore;
    w.updateLauncherVisibility();
  } catch (e) {
    console.warn("[libredesk] show launcher failed:", e && e.message);
  }
}

// ── 启动按钮平时缩进屏幕边缘一半 ─────────────────────────────────────────
// 按钮是 60px 的圆，常驻右下角会盖住页面内容。空闲时整个往屏幕外移出半个，只露出
// 左半边；鼠标移上去滑出来。聊天窗口开着、或有未读消息（右上角红点）时保持完整显示。
//
// 挪的是外层 widgetButtonWrapper（fixed 容器）而不是圆按钮本身：按钮的 transform
// 挂件自己在用（打开聊天时 scale(0.9)），抢同一个属性会互相覆盖。
// 挂件的 init 是异步的（先拉设置再建 DOM），所以要等 wrapper 出现再挂。
const LAUNCHER_WAIT_MS = 30000;
function whenLauncherReady(instance, fn) {
  if (!instance) return;
  const started = Date.now();
  const tick = () => {
    if (instance.widgetButtonWrapper && instance.toggleButton && instance.widgetSettings) {
      fn(instance);
    } else if (Date.now() - started < LAUNCHER_WAIT_MS) {
      setTimeout(tick, 200);
    }
  };
  tick();
}
function setupLauncherPeek(w) {
  const el = w.widgetButtonWrapper;
  const launcher = (w.widgetSettings && w.widgetSettings.launcher) || {};
  const onRight = launcher.position !== "left";
  const sideGap = (launcher.spacing && Number(launcher.spacing.side)) || 0;
  const reducedMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let hovering = false;
  let unread = 0;

  el.style.transition = reducedMotion ? "none" : "transform 0.25s ease";
  const apply = () => {
    const peek = !hovering && !w.isChatVisible && !unread;
    // 移出的距离 = 按钮到屏幕边的间距 + 半个按钮（移动端按钮是 50px，每次现量）
    const shift = sideGap + (w.toggleButton.offsetWidth || 60) / 2;
    el.style.transform = peek ? `translateX(${onRight ? shift : -shift}px)` : "";
  };
  el.addEventListener("mouseenter", () => { hovering = true; apply(); });
  el.addEventListener("mouseleave", () => { hovering = false; apply(); });
  try {
    w.onShow(apply);
    w.onHide(apply);
    w.onUnreadCountChange((n) => { unread = n || 0; apply(); });
  } catch (e) {
    console.warn("[libredesk] launcher peek hooks failed:", e && e.message);
  }
  apply();
}
